import React from 'react'
import get from 'lodash/get'
import DialogActions from '@material-ui/core/DialogActions'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import { MY_PGA_COM_HOST } from 'env'
import Dialog from 'components/dialog'
import BookingUrlField from 'components/share-box/booking-url-field'
import BookingShareActions from 'components/share-box/booking-share-actions'
import ShareBookingUrlAndAccessCodeSection from './share-booking-url-and-access-code-section'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import { useAuth } from 'lib/auth'
import { useHistory } from 'react-router-dom'
import { OnboardingSuccessDialog } from 'components/group-events/create-group-event-form-wizard/steps/success'
import { formatVirtualCoachingCheckoutUrl } from 'pages/pga-coach/bookings/booking-types/offering-kind'

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4, 6),
    },
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5),
  },
  title: {
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  description: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  dialogActions: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 0, 0),
    '& > :not(:first-child)': {
      marginLeft: 0,
    },
  },
}))

const formatBookingTypeUrl = (lessonType, isVirtualCoaching) => {
  if (!lessonType) {
    return null
  }

  if (isVirtualCoaching) {
    return formatVirtualCoachingCheckoutUrl(lessonType)
  }

  const coachId = lessonType?.coach?.externalId
  const coachSlug = get(lessonType, 'coach.coachProfile.slug')
  const lessonTypeId = lessonType.id
  const locationId = get(lessonType, 'facilities[0].id')
  const isLessonPackOffering = lessonType?.lessonPackOfferings?.length > 0

  let bookingUrl = `${MY_PGA_COM_HOST}/coach/${coachSlug ||
    coachId}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}`

  if (isLessonPackOffering) {
    bookingUrl += '&isLessonPacks=true'
  }

  return bookingUrl
}

const BookingTypeFormSuccessDialog = ({
  isOpen,
  setIsOpen,
  onClose,
  onCreateAnother,
  lessonType,
  showOnboardingModal,
  isVirtualCoaching = false,
}) => {
  const classes = useStyles()
  const { user } = useAuth()
  const history = useHistory()
  const bookingTypeUrl = formatBookingTypeUrl(lessonType, isVirtualCoaching)
  const accessCode = lessonType?.accessCode
  let shareDescription =
    'Share your schedule link below with your students so they can book this offering.'
  if (isVirtualCoaching) {
    shareDescription =
      'Share the link below with golfers so they can buy this swing analysis and upload their videos.'
  } else if (accessCode) {
    shareDescription =
      'Share the link below with your students so they can register for this offering. Include the access code if you added one.'
  }

  const handleClose = () => {
    setIsOpen(false)
    onClose()
  }

  const handleFinished = () => {
    handleClose()
    if (user.onboarding) {
      history.push('/pga-coach/set-up-schedule')
    }
  }

  return showOnboardingModal ? (
    <OnboardingSuccessDialog isOpen={isOpen} setIsOpen={setIsOpen} />
  ) : (
    <Dialog
      isOpen={isOpen}
      maxWidth="sm"
      fullWidth
      showCloseButton
      onClose={handleClose}
    >
      {() => (
        <Box className={classes.container}>
          <Box className={classes.iconWrapper}>
            <CircularHighlightedIcon icon={CelebrationOutlinedIcon} />
          </Box>
          <Typography variant="h4" className={classes.title} component="h1">
            Success!
          </Typography>
          <Typography variant="body1" className={classes.description}>
            Your booking type has been created. {shareDescription}
          </Typography>
          {accessCode ? (
            <ShareBookingUrlAndAccessCodeSection
              url={bookingTypeUrl}
              accessCode={accessCode}
              title={lessonType?.title}
            />
          ) : (
            bookingTypeUrl && (
              <>
                <BookingUrlField text={bookingTypeUrl} />
                <BookingShareActions
                  url={bookingTypeUrl}
                  title={lessonType?.title}
                  onText={() =>
                    history.push(
                      `/pga-coach/messages?shared=${encodeURIComponent(
                        bookingTypeUrl,
                      )}&att=pga-lesson-type&id=${lessonType?.id}`,
                    )
                  }
                />
              </>
            )
          )}
          <DialogActions className={classes.dialogActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={onCreateAnother}
            >
              Create Another Booking Type
            </Button>
            <Button color="primary" onClick={handleFinished}>
              I&apos;m Finished
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  )
}

export default BookingTypeFormSuccessDialog
